import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { ArrowLeft } from 'lucide-react';

interface BackButtonProps {
  /** Override the default parent route */
  to?: string;
  /** Label for the button (shown on larger screens) */
  label?: string;
  /** Additional CSS classes */
  className?: string;
}

// Mapping des routes vers leurs parents
const PARENT_ROUTES: Record<string, string> = {
  '/products': '/dashboard',
  '/inventory': '/dashboard',
  '/lots': '/dashboard',
  '/alerts': '/dashboard',
  '/history': '/dashboard',
  '/recipes': '/dashboard',
  '/suppliers': '/dashboard',
  '/settings': '/dashboard',
  '/analytics': '/dashboard',
  '/insights': '/dashboard',
  '/reports': '/dashboard',
  '/global-stocks': '/dashboard',
  '/history-reports': '/history',
  '/inventory-mode': '/inventory',
};

/**
 * Bouton de retour avec navigation prévisible vers la route parente.
 * Préféré à navigate(-1) car plus prévisible pour l'utilisateur.
 *
 * @example
 * <BackButton />
 *
 * @example
 * // Avec destination personnalisée
 * <BackButton to="/products" label="Retour aux produits" />
 */
export function BackButton({ to, label, className = '' }: BackButtonProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = () => {
    if (to) {
      navigate(to);
      return;
    }

    // Trouver la route parente
    const parentRoute = PARENT_ROUTES[location.pathname];
    if (parentRoute) {
      navigate(parentRoute);
      return;
    }

    // Fallback: extraire le parent du pathname
    const segments = location.pathname.split('/').filter(Boolean);
    if (segments.length > 1) {
      segments.pop();
      navigate('/' + segments.join('/'));
      return;
    }

    // Dernier recours: dashboard
    navigate('/dashboard');
  };

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handleBack}
      className={`p-2 flex-shrink-0 ${className}`}
      aria-label="Retour"
    >
      <ArrowLeft size={18} />
      {label && <span className="hidden sm:inline ml-1">{label}</span>}
    </Button>
  );
}
